import { ExcaliburAStar } from "@excaliburjs/plugin-pathfinding";
import { TiledResource } from "@excaliburjs/plugin-tiled";
import {
  DefaultLoader,
  Engine,
  ExcaliburGraphicsContext,
  Query,
  Scene,
  SceneActivationContext,
  TileMap,
} from "excalibur";
import { Building, SerializedBuilding } from "./actors/building";
import { CardManager, SerializedCardManager } from "./deck";
import { CombinableManager } from "./ecs/combinable";
import { HealthSystem } from "./ecs/health";
import { ResourcePerTurnComponent, calculateDelta } from "./ecs/resources";
import { TargetManager } from "./ecs/target";
import { TilePositionManager } from "./ecs/tile";
import { TurnBasedSystem, TurnEvents } from "./ecs/turnBased";
import { BaseFlow } from "./engine/baseFlow";
import { HudEvents, HudInterface } from "./hud";
import { Resources } from "./resources";
import { DynamicResources, GameState, PlayableId, ResourceKind } from "./types";
import { compressString, decompressString } from "./utils/compression";
import { GameStateStorage } from "./utils/gameStateStorage";

export type SerializedGame = {
  buildings: SerializedBuilding[];
  cards: SerializedCardManager;
  gameState: {
    money: number;
    turnCount: number;
    seenResources: ResourceKind[];
    seenTransforms: PlayableId[];
  };

  previous: SerializedGame | undefined;
};

export class MyLevel extends Scene {
  tiledMap!: TiledResource;
  pathing!: ExcaliburAStar;
  pathmap!: TileMap;
  highlightLayer!: TileMap;
  targetManager!: TargetManager;
  tileManager!: TilePositionManager;
  combinableManager!: CombinableManager;
  turnBasedSystem!: TurnBasedSystem;
  hudInterface: HudInterface = new HudInterface(this);
  resourceQuery!: Query<typeof ResourcePerTurnComponent>;

  // Game state tracking
  gameState: GameState = {
    money: 0,
    turnCount: 1,
    seenResources: new Set(),
    seenTransforms: new Set(),
  };

  dynamicResources: DynamicResources = {
    ore: 0,
    food: 0,
    fuel: 0,
    fight: 0,
    money: 0,
  };

  cards: CardManager = new CardManager([
    { name: "pops-1" },
    { name: "fuel-1" },
    { name: "ore-1" },
    { name: "food-1" },
    { name: "fuel-1" },
  ]);

  doorConditions: Map<string, boolean> = new Map([["1", true]]);
  doorConditionsHash: string = "";

  // Restores in place!
  restore(serial: SerializedGame) {
    this.cards.restore(serial.cards);
    this.actors
      .filter((e) => e instanceof Building)
      .forEach((b) => b.kill(true));
    this.gameState = {
      money: serial.gameState.money,
      turnCount: serial.gameState.turnCount || 1,
      seenResources: new Set(serial.gameState.seenResources),
      seenTransforms: new Set(serial.gameState.seenTransforms),
    };

    serial.buildings.forEach((s) => this.add(Building.restore(s)));
    this.snapshotState = serial.previous;
    BaseFlow.CancelCurrentFlow();
    this.engine.clock.schedule(() => this.updateResources(), 10);

    // Emit a custom event to notify UI of state restoration
    window.dispatchEvent(new CustomEvent("gameStateRestored"));
    // Also dispatch after a small delay to ensure React components are listening
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("gameStateRestored"));
    }, 50);
  }

  serialize(): SerializedGame {
    return {
      buildings: this.actors
        .filter((e) => e instanceof Building)
        .map((b) => b.serialize()),
      cards: this.cards.serialize(),
      gameState: {
        money: this.gameState.money,
        turnCount: this.gameState.turnCount,
        seenResources: [...this.gameState.seenResources],
        seenTransforms: [...this.gameState.seenTransforms],
      },
      previous: this.snapshotState,
    };
  }

  snapshotState: SerializedGame | undefined;

  saveUndoSnapshot() {
    this.engine.clock.schedule(() => {
      this.snapshotState = this.serialize();
      GameStateStorage.saveGameState(this.snapshotState);
    });
  }

  undoLastAction() {
    if (!this.snapshotState?.previous) {
      console.log("No further undo state");
      return;
    }

    this.restore(this.snapshotState.previous);
    console.log("restored!");
  }

  async generateShareableUrl(): Promise<string> {
    const serialized = this.serialize();
    const jsonString = JSON.stringify(serialized);
    const compressed = await compressString(jsonString);

    const url = new URL(window.location.href);
    url.hash = `gameState=${compressed}`;

    console.log("Generated shareable URL:", url.toString());
    return url.toString();
  }

  async handleHashChange(): Promise<void> {
    // Check if hash contains gameState
    if (window.location.hash && window.location.hash.includes("gameState=")) {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const gameStateData = hashParams.get("gameState");

      if (gameStateData) {
        console.log("Hash changed with gameState, restoring...");
        const decompressed = await decompressString(gameStateData);
        const serializedGame: SerializedGame = JSON.parse(decompressed);
        this.restore(serializedGame);
        console.log("Successfully restored game state from hash change");

        // Remove the hash after loading
        const cleanUrl =
          window.location.origin +
          window.location.pathname +
          window.location.search;
        window.history.replaceState(null, "", cleanUrl);
      }
    }
  }

  async loadFromUrlIfPresent(): Promise<boolean> {
    // Check hash fragment first (new method)
    let gameStateData: string | null = null;
    let isFromHash = false;

    if (window.location.hash) {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      gameStateData = hashParams.get("gameState");
      if (gameStateData) {
        isFromHash = true;
        console.log("Found gameState in URL hash, attempting to restore...");
      }
    }

    // Fallback to URL params for backward compatibility
    // TODO: Remove this fallback after 2025-10-18 (deprecated as of 2025-09-18)
    if (!gameStateData) {
      const urlParams = new URLSearchParams(window.location.search);
      gameStateData = urlParams.get("gameState");
      if (gameStateData) {
        console.log(
          "Found gameState in URL params (deprecated), attempting to restore...",
        );
      }
    }

    if (gameStateData) {
      const decompressed = await decompressString(gameStateData);
      const serializedGame: SerializedGame = JSON.parse(decompressed);
      this.restore(serializedGame);
      console.log("Successfully restored game state from URL");

      // Clean up URL based on where the data came from
      if (isFromHash) {
        // Remove the hash entirely by constructing clean URL without it
        const cleanUrl =
          window.location.origin +
          window.location.pathname +
          window.location.search;
        window.history.replaceState(null, "", cleanUrl);
      } else {
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete("gameState");
        window.history.replaceState(null, "", cleanUrl.toString());
      }
      return true;
    }

    // Check localStorage if no URL state
    if (GameStateStorage.hasSavedState()) {
      const savedState = await GameStateStorage.loadGameState();
      const turnCount = savedState?.gameState.turnCount;
      if (!turnCount || turnCount == 1) return false;
      const shouldLoad = await GameStateStorage.promptLoadSavedState(turnCount);
      if (shouldLoad && savedState) {
        console.log("Loading saved game state from localStorage");
        this.restore(savedState);
        GameStateStorage.clearSavedState(); // Clear to prevent repeated prompts
        console.log("Successfully restored game state from localStorage");
        return true;
      }
    }

    return false;
  }

  captureStateForTest(): { url: string; serialized: SerializedGame } {
    const serialized = this.serialize();
    console.log("=== GAME STATE FOR TESTING ===");
    console.log("Raw serialized state:", JSON.stringify(serialized, null, 2));

    this.generateShareableUrl().then((url) => {
      console.log("Shareable URL:", url);
      console.log("=== END GAME STATE ===");
    });

    return { url: "", serialized };
  }

  override onInitialize(_engine: Engine): void {
    // Scene.onInitialize is where we recommend you perform the composition for your game
    this.tiledMap = Resources.Map1;
    this.tiledMap.addToScene(this);

    // Set up hash change listener for in-page navigation
    window.addEventListener("hashchange", () => {
      this.handleHashChange();
    });

    // Currently supports only one 'walkable' tilemap

    for (const tilemap of this.tileMaps) {
      if (tilemap.name == "pathing") {
        this.pathmap = tilemap;
        this.pathing = new ExcaliburAStar(tilemap);
      }
      if (tilemap.name == "FloorTerrain") {
        this.highlightLayer = tilemap;
      }
    }
    if (!this.pathing) throw new Error("Requires a Walkable layer");
    if (!this.highlightLayer) throw new Error("Need floor to draw tiles"); //this.floortiles = this.pathing;
    this.tileManager = new TilePositionManager(this, this.pathmap);
    this.combinableManager = new CombinableManager(this, this.tileManager);
    this.world.add(HealthSystem);
    this.turnBasedSystem = new TurnBasedSystem(this.world);
    this.resourceQuery = this.world.query([ResourcePerTurnComponent]);

    this.hudInterface.events.on(
      HudEvents.undoClicked,
      this.undoLastAction.bind(this),
    );
    this.turnBasedSystem.events.on(TurnEvents.Movecomplete, () => {
      console.log("updated");
      this.engine.clock.schedule(() => this.updateResources());
    });
    this.targetManager = new TargetManager(this);

    this.resourceQuery.entityAdded$.subscribe((e) => {
      if (e.get(ResourcePerTurnComponent).includeInSums)
        this.updateResources.bind(this);
    });
    this.resourceQuery.entityRemoved$.subscribe((e) => {
      if (e.get(ResourcePerTurnComponent).includeInSums)
        this.updateResources.bind(this);
    });

    console.log("map intialized");
  }

  override onPreLoad(_loader: DefaultLoader): void {
    console.log("loader loaded");
  }

  override async onActivate(
    _context: SceneActivationContext<unknown>,
  ): Promise<void> {
    // Called when Excalibur transitions to this scene
    // Only 1 scene is active at a time
    // Subscribe to turn complete events to update resources
    this.updateResources();

    // Load state from URL if present
    const loaded = await this.loadFromUrlIfPresent();
    if (loaded) {
      this.updateResources();
    }
  }

  updateResources(): void {
    // Calculate resources using ECS query
    this.dynamicResources = calculateDelta(this.resourceQuery.entities);

    // Update money - both from buildings and population bonus
    this.gameState.money += this.dynamicResources.money || 0;
  }

  override onDeactivate(_context: SceneActivationContext): void {
    // Called when Excalibur transitions away from this scene
    // Only 1 scene is active at a time
    //BaseFlow.CancelCurrentFlow();
    //this.engine.goToScene("start");
    console.log("deactivate");
  }

  override onPreUpdate(_engine: Engine, _elapsedMs: number): void {
    // Called before anything updates in the scene
  }

  override onPostUpdate(_engine: Engine, _elapsedMs: number): void {
    // Called after everything updates in the scene
  }

  override onPreDraw(_ctx: ExcaliburGraphicsContext, _elapsedMs: number): void {
    // Called before Excalibur draws to the screen
  }

  override onPostDraw(
    _ctx: ExcaliburGraphicsContext,
    _elapsedMs: number,
  ): void {
    // Called after Excalibur draws to the screen
  }
}
